<script setup lang="ts">
  import { useFlowStore } from '@/store/flow.store'
  import { computed, onMounted, ref, watch } from 'vue'
  import {
    useXPathAction,
    FormFieldContainer,
    type Nullish,
  } from '@logistiek-zonder-papier/lzp-library'

  const model = defineModel<string | Nullish>({ required: true })

  const flowStore = useFlowStore()

  const {
    label,
    optional = false,
    error,
    warning,
    placeholder,
    validatePaths = false,
    context,
  } = defineProps<{
    label?: string | null
    optional: boolean
    error?: string | null
    warning?: string | null
    placeholder?: string
    validatePaths?: boolean
    context?: Record<string, unknown> | undefined
  }>()

  const xpath = useXPathAction()
  const editor = ref<HTMLDivElement>()

  const TOKEN = /\$\{([^}]+)\}/g

  function stateOf(path: string): 'grey' | 'green' | 'red' {
    if (!validatePaths) return 'grey'

    if (path === 'undefined') return 'green'

    return xpath.resolvePath(path, combinedContext.value) === undefined ? 'red' : 'green'
  }

  const combinedContext = computed(() =>
    context == null
      ? {
          ...flowStore.context,
          flowData: () => ({ ...flowStore.flowData }),
        }
      : context
  )

  function titleOf(path: string): string {
    const value = xpath.resolvePath(path, combinedContext.value)
    return value === undefined ? 'undefined' : JSON.stringify(value)
  }

  function makeChip(path: string): HTMLSpanElement {
    const chip = document.createElement('span')
    chip.className = `path-chip ${stateOf(path)}`
    chip.contentEditable = 'false'
    chip.dataset['path'] = path
    chip.title = titleOf(path)
    chip.textContent = path
    return chip
  }

  function render() {
    const el = editor.value
    if (!el) return
    el.replaceChildren()
    const text = model.value ?? ''
    let last = 0
    for (const match of text.matchAll(TOKEN)) {
      const index = match.index ?? 0
      if (index > last) el.appendChild(document.createTextNode(text.slice(last, index)))
      el.appendChild(makeChip(match[1] ?? ''))
      last = index + match[0].length
    }
    if (last < text.length) el.appendChild(document.createTextNode(text.slice(last)))
  }

  function serialize(): string {
    const el = editor.value
    if (!el) return ''
    let out = ''
    el.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        out += node.textContent ?? ''
      } else if (node instanceof HTMLElement && node.classList.contains('path-chip')) {
        out += '${' + (node.dataset['path'] ?? '') + '}'
      } else {
        out += node.textContent ?? ''
      }
    })
    return out
  }

  function onInput() {
    model.value = serialize()
  }

  function recolor() {
    editor.value?.querySelectorAll<HTMLElement>('.path-chip').forEach(chip => {
      const path = chip.dataset['path'] ?? ''
      chip.className = `path-chip ${stateOf(path)}`
      chip.title = titleOf(path)
    })
  }

  function caretOffsetInChip(chip: HTMLElement, clientX: number, clientY: number): number {
    const length = (chip.textContent ?? '').length
    let range: Range | null = null
    if (document.caretRangeFromPoint) {
      range = document.caretRangeFromPoint(clientX, clientY)
    } else if ('caretPositionFromPoint' in document) {
      const pos = document.caretPositionFromPoint(clientX, clientY)
      if (pos) {
        range = document.createRange()
        range.setStart(pos.offsetNode, pos.offset)
      }
    }
    if (range && chip.contains(range.startContainer)) {
      return Math.min(range.startOffset, length)
    }
    return length
  }

  function onChipMouseDown(event: MouseEvent) {
    const target = event.target as HTMLElement
    const chip = target.closest<HTMLElement>('.path-chip')
    if (!chip || !editor.value?.contains(chip)) return

    event.preventDefault()
    const path = chip.dataset['path'] ?? ''
    const caretOffset = caretOffsetInChip(chip, event.clientX, event.clientY)
    const raw = '${' + path + '}'
    const textNode = document.createTextNode(raw)
    chip.replaceWith(textNode)

    const range = document.createRange()
    const selection = window.getSelection()
    range.setStart(textNode, Math.min(caretOffset + 2, raw.length))
    range.collapse(true)
    selection?.removeAllRanges()
    selection?.addRange(range)
    editor.value?.focus()
  }

  onMounted(render)

  // external model change → re-render. Self-input is skipped (DOM already matches).
  watch(model, value => {
    if (editor.value && serialize() !== (value ?? '')) render()
  })
  // validation toggle / context change → just recolor the existing chips
  watch(() => validatePaths, recolor)
  watch(() => context, recolor, { deep: true })
</script>

<template>
  <FormFieldContainer :label="label" :optional="optional" :error="error" :warning="warning">
    <div
      ref="editor"
      class="path-input"
      contenteditable="true"
      spellcheck="false"
      :data-placeholder="placeholder"
      @input="onInput"
      @blur="render"
      @mousedown="onChipMouseDown"
    ></div>
  </FormFieldContainer>
</template>

<style scoped>
  .path-input {
    display: block;
    width: 100%;
    min-height: var(--components-component-height, 2.5rem);
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--colours-grey-3);
    border-radius: 0.375rem;
    background: var(--colours-grey-1);
    font-size: 0.875rem;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
    outline: none;
    align-content: center;
  }

  .path-input:focus {
    border-color: var(--colours-brand-colour-1);
  }

  .path-input:empty::before {
    content: attr(data-placeholder);
    color: var(--colours-grey-4);
  }
</style>

<style>
  /* Not scoped: these target nodes created via createElement, which never
   receive the data-v scope attribute. */
  .path-chip {
    display: inline-block;
    padding: 0 0.375rem;
    margin: 0 0.0625rem;
    border-radius: 0.25rem;
    font-family: ui-monospace, monospace;
    font-size: 0.8125rem;
    line-height: 1.4;
    cursor: default;
    user-select: none;
  }

  .path-chip.grey {
    background: var(--colours-grey-2);
    color: var(--colours-grey-4);
  }

  .path-chip.green {
    background: rgb(209, 250, 229);
    color: rgb(6, 95, 70);
  }

  .path-chip.red {
    background: rgb(254, 226, 226);
    color: rgb(153, 27, 27);
  }
</style>
